// DateFormatUtility.ts - Contains utilities for date formatting across the application
// Import the CountryProfileService
import { CountryProfileService } from "../services/CountryProfiles/CountryProfileService";

// Date format options available in the dropdown
export interface DateFormatOption {
  key: string;
  text: string;
  example: string;
}


// Date format options for dropdown
export const dateFormatOptions: DateFormatOption[] = [
  { key: "MM/DD/YYYY", text: "MM/DD/YYYY", example: "10/16/2025" },
  { key: "DD/MM/YYYY", text: "DD/MM/YYYY", example: "16/10/2025" },
  { key: "YYYY/MM/DD", text: "YYYY/MM/DD", example: "2025/10/16" },
  { key: "YYYY-MM-DD", text: "YYYY-MM-DD", example: "2025-10-16" },
  { key: "DD.MM.YYYY", text: "DD.MM.YYYY", example: "16.10.2025" },
  { key: "DD-MM-YYYY", text: "DD-MM-YYYY", example: "16-10-2025" },
];

// Function to get the default date format for a country - case insensitive
// Now: Japan = YYYY-MM-DD, all others = DD-MM-YYYY
export const getDefaultDateFormatForCountry = (countryName: string): string => {
  if (!countryName) return "DD-MM-YYYY";
  const normalizedCountryName = countryName.toLowerCase().trim();
  if (normalizedCountryName === "japan") {
    return "YYYY-MM-DD";
  }
  return "DD-MM-YYYY";
};

// Function to format a date according to the specified format pattern
export const formatDateWithPattern = (date: Date, format: string): string => {
  const year = date.getFullYear().toString();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  
  switch (format) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'YYYY/MM/DD':
      return `${year}/${month}/${day}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'DD.MM.YYYY':
      return `${day}.${month}.${year}`;
    case 'DD-MM-YYYY':
      return `${day}-${month}-${year}`;
    default:
      return `${day}/${month}/${year}`; // Default to DD/MM/YYYY
  }
};

// Function to parse a date string according to the specified format
export const parseDateFromFormatted = (dateStr: string, format: string): Date | null => {
  if (!dateStr) {
    console.warn("Empty date string passed to parseDateFromFormatted");
    return null;
  }
  
  try {
    let day: number, month: number, year: number;
    let parts: number[];
    let separator: string;
    
    // Detect the separator used in the date string
    if (dateStr.includes('/')) {
      separator = '/';
    } else if (dateStr.includes('-')) {
      separator = '-';
    } else if (dateStr.includes('.')) {
      separator = '.';
    } else {
      console.warn(`Could not detect separator in date string: ${dateStr}`);
      return new Date(dateStr); // Fall back to standard parsing
    }
    
    // Split the string using the detected separator
    parts = dateStr.split(separator).map(part => parseInt(part.trim(), 10));
    
    // If any part couldn't be parsed to a number, log and return null
    if (parts.some(isNaN)) {
      console.warn(`Invalid date parts in string: ${dateStr}`);
      return null;
    }
    
    switch (format) {
      case 'MM/DD/YYYY':
        [month, day, year] = parts;
        break;
      
      case 'DD/MM/YYYY':
        [day, month, year] = parts;
        break;
      
      case 'YYYY/MM/DD':
        [year, month, day] = parts;
        break;
      
      case 'YYYY-MM-DD':
        [year, month, day] = parts;
        break;
      
      case 'DD.MM.YYYY':
        [day, month, year] = parts;
        break;
      
      case 'DD-MM-YYYY':
        [day, month, year] = parts;
        break;
      
      default:
        console.warn(`Unknown date format: ${format}, attempting to detect format`);
        
        // Attempt to detect the format based on the values
        if (parts.length === 3) {
          // If first part is > 31, it's likely a year
          if (parts[0] > 31) {
            [year, month, day] = parts;
          } 
          // If last part is > 31, it's likely a year
          else if (parts[2] > 31) {
            // Check month/day order
            if (parts[0] > 12 && parts[1] <= 12) {
              [day, month, year] = parts;
            } else {
              [month, day, year] = parts;
            }
          } else {
            // Default to DD/MM/YYYY as a common international format
            [day, month, year] = parts;
          }
        } else {
          console.error("Unable to parse date with unexpected number of parts:", dateStr);
          return null;
        }
    }
    
    // Validate the date parts
    if (month < 1 || month > 12) {
      console.warn(`Invalid month in date: ${month}`);
      return null;
    }
    
    if (day < 1 || day > 31) {
      console.warn(`Invalid day in date: ${day}`);
      return null;
    }
    
    // Create and validate the date
    const result = new Date(year, month - 1, day);
    
    // Check if the date is valid (e.g., not "February 31st")
    if (result.getFullYear() !== year || result.getMonth() !== month - 1 || result.getDate() !== day) {
      console.warn(`Resulting date is invalid: ${result} from parts year=${year}, month=${month}, day=${day}`);
      return null;
    }
    
    return result;
  } catch (e) {
    console.error("Error parsing date:", e);
    return null;
  }
};

// Function to get a country's date format from the API
// This should be used instead of localStorage for country-specific date formats
export const getCountryDateFormat = async (countryName: string): Promise<string | null> => {
  try {
    if (!countryName) {
      console.warn('getCountryDateFormat called with empty country name');
      return null;
    }
    
    console.log(`Requesting date format for country: ${countryName}`);
    
    // Call the API using the CountryProfileService
    const response = await CountryProfileService.getDateFormat(countryName);
    
    // Check if the response is successful
    if (response && response.status === 200 && response.data) {
      // API response structure is typically { code: number, data: any, message: string }
      const apiData = response.data;
      
      if (apiData.data) {
        return apiData.data;
      }
    }
    
    console.warn(`No date format returned from API for ${countryName}:`, response);

  // If no API value, use the new default logic
  const defaultFormat = getDefaultDateFormatForCountry(countryName);
  console.log(`Using default date format for ${countryName}: ${defaultFormat}`);
  return defaultFormat;
  } catch (error) {
    console.error(`Error fetching date format for ${countryName}:`, error);

    // If all else fails, return the new default logic
    return getDefaultDateFormatForCountry(countryName);
  }
};
// Format a Date object to MM/DD/YYYY string (for storage)
export const formatDateToMMDDYYYY = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear().toString();
  return `${month}/${day}/${year}`;
};